import axios, { type AxiosResponse } from 'axios';

import { getUserData, VESRAPI } from '../constants/global';
import type { IChecklistStepTemplate, IObserverChecklist, IRecordChecklistStep } from '../interface/observer-checklist';
import type { ICreateIncidentRequest, IIncident, IIncidentListResponse, INearbyResponse, IVesrOutcome } from '../interface/vesr';
import { resolveVesrBearer } from './vesr-auth';

// Standalone client for the VESR Incidents API (own subdomain, not the
// gateway). Auth: a VESR-issued token where the exchange endpoint exists
// (staging — see vesr-auth.ts), otherwise the core JWT which production VESR
// normalizes server-side. NO 401->logout interceptor: a VESR outage must
// never sign the agent out (same discipline as feedback-api /
// agent-assignment-api).
// timeout: 0 (no timeout) is DELIBERATE and load-bearing. Video evidence is
// capped at 500MB and agents upload from the field, so a single request can
// legitimately run for many minutes. Adding a "sensible" default here would
// silently break large uploads with no error the agent can act on.
const vesrApi = axios.create({ baseURL: VESRAPI, timeout: 0 });

vesrApi.interceptors.request.use(async (config) => {
	const bearer = await resolveVesrBearer();
	if (bearer) config.headers.Authorization = `Bearer ${bearer}`;
	const { user } = getUserData();
	// Independent observers have no party; VESR scopes them by their token's organisation.
	if (user?.PartyId && user.PartyId !== '0') config.headers.PartyId = user.PartyId;
	return config;
});

// Coerce string bodies back to JSON when the Content-Type prevents axios from
// auto-parsing (same guard as the other standalone clients).
vesrApi.interceptors.response.use((response) => {
	if (typeof response.data === 'string' && response.data) {
		try {
			response.data = JSON.parse(response.data);
		} catch {
			// Not JSON — leave as-is.
		}
	}
	return response;
});

const INCIDENTS = '/api/v1/vesr/incidents';

/**
 * Wraps an axios response into the outcome shape, classifying 429s (the whole
 * incidents controller is rate-limited at 30 req/min/IP) and surfacing the
 * `{ message }` error bodies VESR returns on non-2xx.
 */

/**
 * Pull a message out of an ASP.NET ProblemDetails body.
 *
 * VESR returns `{ message }` from its own handlers, but framework-level
 * failures (model binding, request-size limits) bypass those and come back as
 * ProblemDetails with the text buried in `errors[""][0]`. Without this the
 * whole body reads as "no message" — which is how a request-too-large 400
 * looked indistinguishable from a transient failure.
 */
export const problemDetailsMessage = (data: unknown): string | undefined => {
	if (!data || typeof data !== 'object') return undefined;
	const { errors, title } = data as { errors?: Record<string, unknown>; title?: unknown };
	if (errors && typeof errors === 'object') {
		for (const value of Object.values(errors)) {
			if (Array.isArray(value) && typeof value[0] === 'string') return value[0];
		}
	}
	return typeof title === 'string' ? title : undefined;
};
const toOutcome = <T>(res: AxiosResponse): IVesrOutcome<T> => {
	const ok = res.status >= 200 && res.status < 300;
	const outcome: IVesrOutcome<T> = {
		reachable: true,
		httpStatus: res.status,
		body: ok ? (res.data as T) : undefined,
	};
	if (!ok) {
		const message = res.data?.error?.message || res.data?.message || problemDetailsMessage(res.data);
		if (typeof message === 'string') outcome.errorMessage = message;
	}
	if (res.status === 429) {
		const fromBody = Number(res.data?.error?.retryAfterSeconds);
		const fromHeader = Number(res.headers?.['retry-after']);
		const retryAfter = Number.isFinite(fromBody) ? fromBody : Number.isFinite(fromHeader) ? fromHeader : 60;
		outcome.retryAfterSeconds = retryAfter;
	}
	const replayed = res.headers?.['x-idempotency-replayed'];
	if (replayed === 'true' || replayed === true) outcome.replayed = true;
	return outcome;
};

/**
 * Create an incident. `idempotencyKey` must be stable across retries of the
 * same logical submission (the server replays the cached 2xx instead of
 * creating a duplicate) and fresh for each new logical submission.
 */
export const createIncident = async (body: ICreateIncidentRequest, idempotencyKey: string): Promise<IVesrOutcome<IIncident>> => {
	try {
		const res = await vesrApi.post(INCIDENTS, body, {
			headers: { 'X-Idempotency-Key': idempotencyKey },
			validateStatus: () => true,
		});
		return toOutcome<IIncident>(res);
	} catch {
		return { reachable: false };
	}
};

/**
 * Attach media to an existing incident (the endpoint path says `images` but
 * the server also accepts mp4/webm/mov video). Multipart field name is
 * `files` (repeatable). Caller pre-validates: ≤10 files per request, images
 * ≤10MB, video ≤500MB and max 1 per incident. Returns the updated incident.
 * Send a video ALONE in its own request: the server's request limit leaves no
 * room for a 500MB clip plus photos.
 *
 * A size rejection comes back as 400 with `{ message: "... exceeds maximum
 * size of 500 MB" }`, or as a body-less 413 if an edge proxy cuts it first.
 * Both are terminal — see the size branch in submit-machine.ts.
 */
export const uploadIncidentImages = async (
	id: string,
	files: File[],
	onProgress?: (percent: number) => void
): Promise<IVesrOutcome<IIncident>> => {
	const fd = new FormData();
	files.forEach((file) => fd.append('files', file));
	try {
		const res = await vesrApi.post(`${INCIDENTS}/${id}/images`, fd, {
			validateStatus: () => true,
			onUploadProgress: (event) => {
				if (onProgress && event.total) onProgress(Math.round((event.loaded / event.total) * 100));
			},
		});
		return toOutcome<IIncident>(res);
	} catch {
		return { reachable: false };
	}
};

export interface IListIncidentsParams {
	status?: string;
	category?: string;
	severity?: string;
	stateCode?: string;
	search?: string;
	page?: number;
	pageSize?: number;
	/**
	 * Server-side reporter filter. The server only allows a non-admin to pass
	 * their OWN id, so this is scoping, not a lookup of other people's reports.
	 */
	reportedBy?: string;
	/** Core election id (string form, as stored on the incident). */
	electionId?: string;
}

/**
 * List incidents (paged envelope `{ data, pagination }`). Pass `reportedBy` for
 * "My Reports": filtering client-side over a page of the global feed loses an
 * agent's own reports as soon as the feed is busier than one page.
 */
export const listIncidents = async (params: IListIncidentsParams = {}): Promise<IVesrOutcome<IIncidentListResponse>> => {
	try {
		const res = await vesrApi.get(INCIDENTS, { params, validateStatus: () => true });
		return toOutcome<IIncidentListResponse>(res);
	} catch {
		return { reachable: false };
	}
};

export const getIncident = async (id: string): Promise<IVesrOutcome<IIncident>> => {
	try {
		const res = await vesrApi.get(`${INCIDENTS}/${id}`, { validateStatus: () => true });
		return toOutcome<IIncident>(res);
	} catch {
		return { reachable: false };
	}
};

export const getNearbyIncidents = async (
	latitude: number,
	longitude: number,
	radiusKm = 10,
	maxResults = 20
): Promise<IVesrOutcome<INearbyResponse>> => {
	try {
		const res = await vesrApi.get(`${INCIDENTS}/nearby`, {
			params: { latitude, longitude, radiusKm, maxResults },
			validateStatus: () => true,
		});
		return toOutcome<INearbyResponse>(res);
	} catch {
		return { reachable: false };
	}
};

const REPORTED_BY_KEY = 'vesrReportedBy';

/**
 * The agent's deterministic VESR GUID is only learnable from a create
 * response (`reportedBy`). Persist it so "My Reports" can client-filter the
 * list across sessions.
 */
export const rememberReportedBy = (reportedBy?: string | null) => {
	if (!reportedBy) return;
	try {
		localStorage.setItem(REPORTED_BY_KEY, reportedBy);
	} catch {
		// Storage unavailable — the in-session create response still covers us.
	}
};

export const getRememberedReportedBy = (): string | null => {
	try {
		return localStorage.getItem(REPORTED_BY_KEY);
	} catch {
		return null;
	}
};

// ---- Independent observer checklists -------------------------------------------------------
// api.vesr `observer-checklists`, behind Vesr:ObserverReporting:Enabled. A 403 means the flag is
// off or the caller is not a field observer; a 404 on record means "not posted to that unit";
// a 503 means api.core could not confirm the posting (fails closed — retry later).

const CHECKLISTS = '/api/v1/vesr/observer-checklists';

export const getChecklistTemplate = async (): Promise<IVesrOutcome<{ data: IChecklistStepTemplate[] }>> => {
	try {
		return toOutcome(await vesrApi.get(`${CHECKLISTS}/template`, { validateStatus: () => true }));
	} catch {
		return { reachable: false };
	}
};

export const getMyChecklists = async (electionId?: number): Promise<IVesrOutcome<{ data: IObserverChecklist[] }>> => {
	try {
		return toOutcome(
			await vesrApi.get(`${CHECKLISTS}/mine`, { params: electionId ? { electionId } : undefined, validateStatus: () => true })
		);
	} catch {
		return { reachable: false };
	}
};

export const recordChecklistStep = async (
	electionId: number,
	entityCode: string,
	stepKey: string,
	body: IRecordChecklistStep
): Promise<IVesrOutcome<{ data: IObserverChecklist }>> => {
	try {
		const path = `${CHECKLISTS}/${electionId}/${encodeURIComponent(entityCode)}/steps/${encodeURIComponent(stepKey)}`;
		return toOutcome(await vesrApi.put(path, body, { validateStatus: () => true }));
	} catch {
		return { reachable: false };
	}
};
